/**
 * Reading a Generation document.
 *
 * Every function here takes the canonical JSON the API returns and produces
 * display values. Nothing fetches, nothing touches the DOM, and nothing
 * measures: the platform already measured the mesh, and §4 of the direction is
 * explicit that a quality figure is "the Skill's own quality report, never
 * inferred". So this module reads the report, decides what to *show*, and
 * refuses to compute an engineering number the backend did not send.
 *
 * The one place that distinction gets subtle is the verdict. There is no
 * `valid` field on `MeshQualityReport` and this module does not invent one:
 * `verdict` counts the report's own findings by their own severities. "2
 * warnings" is a restatement; "Valid" would be a judgement, and a judgement
 * about a mesh belongs to whatever analyses it.
 */

/** Which end of a metric is the bad end. Mirrors `MetricOrientation`. */
const HIGHER_IS_BETTER = 'higher_is_better';

/** Severity slugs, from `Severity` in the quality schema. */
export const ERROR = 'error';
export const WARNING = 'warning';

/**
 * The value of the worst element under a metric's own orientation.
 *
 * A mesh's quality is mostly told by its worst element, and which end that is
 * depends on the metric: the smallest `minSICN` is the worst element, the
 * largest aspect ratio is. Reading `min` unconditionally would report the best
 * element of a lower-is-better metric as its worst.
 */
export function worstOf(summary) {
  if (!summary) return null;
  const orientation = summary.orientation || HIGHER_IS_BETTER;
  return orientation === HIGHER_IS_BETTER ? summary.min : summary.max;
}

/**
 * Every metric in the report, with its worst value resolved.
 *
 * Sorted by name so a panel does not reorder itself between two readings of
 * the same mesh. Metric names are data -- a second mesher naming a metric this
 * one does not is a value change, not a schema change -- so nothing here has a
 * list of expected names.
 */
export function metricRows(report) {
  const metrics = report?.metrics || {};
  return Object.keys(metrics)
    .sort()
    .map((name) => ({
      name,
      ...metrics[name],
      orientation: metrics[name].orientation || HIGHER_IS_BETTER,
      worst: worstOf(metrics[name]),
    }));
}

/**
 * The report's findings, counted by severity.
 *
 * `tone` drives the colour of one word in the status strip, so it is derived
 * here rather than in the DOM code: the rule "an error outranks a warning" is
 * about the report, not about styling.
 */
export function verdict(report) {
  if (!report) return { label: 'Not measured', tone: 'unknown', errors: 0, warnings: 0 };
  const findings = report.findings || [];
  const errors = findings.filter((finding) => finding.severity === ERROR).length;
  const warnings = findings.filter((finding) => finding.severity === WARNING).length;
  if (errors > 0) {
    return { label: plural(errors, 'error'), tone: 'error', errors, warnings };
  }
  if (warnings > 0) {
    return { label: plural(warnings, 'warning'), tone: 'warn', errors, warnings };
  }
  return { label: 'No findings', tone: 'ok', errors: 0, warnings: 0 };
}

function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** The bounding box as three spans, or null. Used for the model's extent. */
export function extentOf(boundingBox) {
  if (!boundingBox || !boundingBox.min || !boundingBox.max) return null;
  const { min, max } = boundingBox;
  if (min.length !== 3 || max.length !== 3) return null;
  return [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
}

/**
 * The whole Generation, as the panels read it.
 *
 * Tolerant of an absent quality report on purpose: a `failed` Generation has
 * none, and it is still a Generation that the history lists and the panel
 * explains. §10 -- a failed attempt is provenance, not an absence.
 */
export function summarise(generation) {
  const report = generation?.quality_report || null;
  return {
    id: generation?.id || null,
    status: generation?.status || 'unknown',
    ok: generation?.status === 'ok',
    // Elements of the mesh's own dimension: tetrahedra for a 3D mesh. Named
    // "cells" in the UI because that is what a CFD user counts, but it is the
    // report's `element_count` unmodified.
    cells: report?.element_count ?? null,
    dimension: report?.dimension ?? null,
    metrics: metricRows(report),
    unmeasured: report?.unmeasured || {},
    findings: report?.findings || [],
    verdict: verdict(report),
    extent: extentOf(report?.bounding_box),
    boundingBox: report?.bounding_box || null,
    measuredBy: report?.measured_by || null,
    meshRef: generation?.mesh_ref || null,
    geoScriptRef: generation?.geo_script_ref || null,
    inputHash: generation?.input_hash || null,
    modelSnapshotHash: generation?.model_snapshot_hash || null,
    parentId: generation?.parent_generation_id || null,
    skill: generation?.skill_name
      ? `${generation.skill_name} ${generation.skill_version || ''}`.trim()
      : null,
    configuration: generation?.configuration || null,
    boundaryMap: generation?.boundary_map || {},
    error: generation?.error || null,
  };
}

/**
 * What a user can take away from this Generation.
 *
 * Only the endpoints the platform actually serves. `geo_script_ref` is
 * deliberately listed as a recorded reference with no link: Step 9 named that
 * gap -- the ref is canonical provenance and no route resolves it -- and
 * showing it as unavailable is honest where hiding it would quietly lose a
 * field the record carries.
 */
export function artifactRows(generation, endpoints) {
  const summary = summarise(generation);
  const rows = [
    {
      key: 'mesh',
      label: 'Canonical mesh',
      detail: 'Gmsh MSH 2.2',
      href: summary.meshRef && endpoints ? endpoints.download : null,
      ref: summary.meshRef,
    },
    {
      key: 'surface',
      label: 'Render surface',
      detail: 'Packed triangles',
      href: summary.meshRef && endpoints ? endpoints.surface : null,
      ref: summary.meshRef,
    },
    {
      key: 'quality',
      label: 'Quality report',
      detail: summary.metrics.length
        ? `${summary.metrics.length} metric${summary.metrics.length === 1 ? '' : 's'}`
        : 'Not measured',
      // Carried inside the Generation document rather than served separately,
      // so the Generation itself is the download.
      href: summary.cells === null || !endpoints ? null : endpoints.generation,
      ref: null,
    },
  ];
  if (summary.geoScriptRef) {
    rows.push({
      key: 'geo',
      label: 'Geometry script',
      detail: 'Recorded, not served',
      href: null,
      ref: summary.geoScriptRef,
    });
  }
  return rows;
}

/**
 * Order Generations for the history rail and number them.
 *
 * The ordinal is a position in *this* list, not an identity: Generations are
 * identified by uuid and the platform mints no sequence. Numbering here keeps
 * "Generation 05" a reading aid rather than a second identifier that could
 * disagree with the record.
 */
export function historyRows(envelopes) {
  const ascending = [...envelopes].sort((left, right) =>
    String(left.created_at).localeCompare(String(right.created_at)),
  );
  const ordinals = new Map(ascending.map((envelope, index) => [envelope.generation_id, index + 1]));
  return [...envelopes]
    .sort((left, right) => String(right.created_at).localeCompare(String(left.created_at)))
    .map((envelope) => ({
      id: envelope.generation_id,
      ordinal: ordinals.get(envelope.generation_id),
      status: envelope.status,
      createdAt: envelope.created_at,
      cells: envelope.generation?.quality_report?.element_count ?? null,
      parentId: envelope.generation?.parent_generation_id || null,
      generation: envelope.generation,
    }));
}
